import { useState, useEffect, useMemo, useRef } from "react";
import { Check, Search } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getLocationStatsRpc, getLocationCoords } from "@/data/raw";
import { getDeviceLocation, getCachedCoords } from "@/lib/geolocation";
import { cn } from "@/lib/utils";
import { logError } from "@/lib/logError";

interface Location {
  location_id: string;
  location_name: string;
  location_slug: string;
  location_type: string;
  checkins_count: number;
  unique_burgers_count: number;
  lat: number | null;
  lng: number | null;
}

interface LocationSearchProps {
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  /** Show the static "Search Burgers Near You" label (nav-style trigger). */
  showLabel?: boolean;
  /** Show the currently selected location name in the trigger. */
  showValue?: boolean;
}

const TOP_N = 7;

export const LocationSearch = ({
  value,
  onValueChange,
  className,
  showLabel = false,
  showValue = true,
}: LocationSearchProps) => {
  const [open, setOpen] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [hasTyped, setHasTyped] = useState(false);
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(
    () => getCachedCoords(),
  );
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadLocations();
    if (!userCoords) {
      getDeviceLocation({ enableHighAccuracy: false, timeout: 5000 })
        .then((c) => c && setUserCoords(c))
        .catch(() => {});
    }
  }, []);

  const loadLocations = async () => {
    setLoading(true);
    try {
      const [statsRes, coordsRes] = await Promise.all([
        getLocationStatsRpc(),
        getLocationCoords(),
      ]);
      if (statsRes.error) {
        logError(statsRes.error, { where: "Error loading locations" });
        return;
      }
      const coordsMap = new Map<string, { lat: number | null; lng: number | null }>();
      for (const r of coordsRes.data ?? []) {
        coordsMap.set(r.id as string, { lat: r.lat as number | null, lng: r.lng as number | null });
      }
      const merged: Location[] = (statsRes.data ?? []).map((l: any) => ({
        ...l,
        lat: coordsMap.get(l.location_id)?.lat ?? null,
        lng: coordsMap.get(l.location_id)?.lng ?? null,
      }));
      setLocations(merged);
    } catch (error) {
      logError(error, { where: "Error loading locations" });
    } finally {
      setLoading(false);
    }
  };

  const getDisplayValue = () => {
    if (!value || value === "world") return showLabel ? "" : "Worldwide";
    const location = locations.find((l) => l.location_slug === value);
    return location ? location.location_name : showLabel ? "" : "Worldwide";
  };

  // Focus the input when the popover opens (no prefill — just a placeholder).
  useEffect(() => {
    if (open) {
      setSearch("");
      setHasTyped(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const getLocationIcon = (type: string) => {
    switch (type) {
      case "country":
        return "🌍";
      case "city":
        return "🏙️";
      case "administrative_area_level_1":
        return "🏞️";
      default:
        return "📍";
    }
  };

  const pickTop = (items: Location[]): Location[] => {
    if (userCoords) {
      const withDist = items
        .filter((l) => l.lat != null && l.lng != null)
        .map((l) => ({
          l,
          dist: (l.lat! - userCoords.lat) ** 2 + (l.lng! - userCoords.lng) ** 2,
        }))
        .sort((a, b) => a.dist - b.dist)
        .slice(0, TOP_N)
        .map((x) => x.l);
      if (withDist.length >= TOP_N) return withDist;
      const chosen = new Set(withDist.map((x) => x.location_id));
      const fallback = [...items]
        .filter((l) => !chosen.has(l.location_id))
        .sort((a, b) => b.checkins_count - a.checkins_count)
        .slice(0, TOP_N - withDist.length);
      return [...withDist, ...fallback];
    }
    return [...items]
      .sort((a, b) => b.checkins_count - a.checkins_count)
      .slice(0, TOP_N);
  };

  const { topCities, topCountries, otherLocations } = useMemo(() => {
    const cities = locations.filter((l) => l.location_type === "city");
    const countries = locations.filter((l) => l.location_type === "country");
    const others = locations.filter(
      (l) => l.location_type !== "city" && l.location_type !== "country",
    );
    return {
      topCities: pickTop(cities),
      topCountries: pickTop(countries),
      otherLocations: others,
    };
  }, [locations, userCoords]);

  const isSearching = hasTyped && search.trim().length > 0;

  const handleSearchChange = (val: string) => {
    setSearch(val);
    setHasTyped(true);
  };

  const selectLocation = (slug: string) => {
    onValueChange(slug);
    setOpen(false);
    setSearch("");
    setHasTyped(false);
  };

  const renderItem = (location: Location) => (
    <CommandItem
      key={location.location_id}
      value={`${location.location_name} ${location.location_slug}`}
      onSelect={() => selectLocation(location.location_slug)}
    >
      <Check
        className={cn(
          "mr-2 h-4 w-4",
          value === location.location_slug ? "opacity-100" : "opacity-0",
        )}
      />
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2">
          <span>{getLocationIcon(location.location_type)}</span>
          <span>{location.location_name}</span>
        </div>
        <div className="text-sm text-muted-foreground">
          {location.checkins_count} check-ins
        </div>
      </div>
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setHasTyped(false); } }}>
      <PopoverTrigger asChild>
        <div
          role="combobox"
          aria-expanded={open}
          aria-label="Search burgers in a location"
          tabIndex={0}
          className={cn(
            "flex items-center gap-2 rounded-md border border-input bg-card px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-ring",
            className,
          )}
        >
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          {showLabel && (
            <span className="truncate text-muted-foreground">Search Burgers Near You</span>
          )}
          {showValue && !!getDisplayValue() && (
            <span className="truncate font-medium text-foreground">{getDisplayValue()}</span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[min(400px,calc(100vw-2rem))] max-w-[400px] p-0 bg-white border shadow-lg z-50"
      >
        <Command>
          <CommandInput
            ref={inputRef}
            placeholder="Type a location"
            value={search}
            onValueChange={handleSearchChange}
          />
          <CommandList>
            {isSearching ? (
              <>
                <CommandEmpty>
                  {loading ? "Loading locations..." : "No locations found."}
                </CommandEmpty>

                <CommandGroup heading="Cities">
                  {locations.filter((l) => l.location_type === "city").map(renderItem)}
                </CommandGroup>
                <CommandGroup heading="Countries">
                  {locations.filter((l) => l.location_type === "country").map(renderItem)}
                </CommandGroup>
                {otherLocations.length > 0 && (
                  <CommandGroup heading="States & Provinces">
                    {otherLocations.map(renderItem)}
                  </CommandGroup>
                )}
              </>
            ) : (
              <>
                {loading && (
                  <div className="py-6 px-4 text-center text-sm text-muted-foreground">
                    Loading locations...
                  </div>
                )}
                <CommandGroup heading="Top locations">
                  <CommandItem
                    value="worldwide world"
                    onSelect={() => selectLocation("world")}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === "world" ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <div className="flex items-center gap-2">
                      <span>🌐</span>
                      <span>Worldwide</span>
                    </div>
                  </CommandItem>
                  {topCities.map(renderItem)}
                  {topCountries.map(renderItem)}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
